import { useState } from 'react';
import { FolderOpen, Lightbulb, Plus, Trash2 } from 'lucide-react';
import type { Proyecto } from '../../types';
import { useIdeas } from '../../hooks/useIdeas';
import { nuevoUUID } from '../../lib/uuid';
import { FormularioProyecto } from './FormularioProyecto';
import { TarjetaProyecto } from './TarjetaProyecto';
import { VistaProyecto } from './VistaProyecto';
import { DialogoConfirmacion } from '../ui/DialogoConfirmacion';

export function SeccionIdeas() {
  const {
    proyectos,
    ideas,
    guardarProyecto,
    eliminarProyecto,
    guardarIdea,
    alternarHecha,
    eliminarIdea,
  } = useIdeas();

  const [proyectoAbiertoId, setProyectoAbiertoId] = useState<string | null>(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [editando, setEditando] = useState<Proyecto | null>(null);
  const [proyectoAEliminar, setProyectoAEliminar] = useState<Proyecto | null>(null);

  // Se resuelve contra la lista viva: si el proyecto abierto se borra desde otro
  // dispositivo, la pantalla regresa sola a la lista de proyectos.
  const proyectoAbierto = proyectos.find((p) => p.id === proyectoAbiertoId) ?? null;

  if (proyectoAbierto) {
    return (
      <VistaProyecto
        key={proyectoAbierto.id}
        proyecto={proyectoAbierto}
        ideas={ideas.filter((i) => i.proyectoId === proyectoAbierto.id)}
        onVolver={() => setProyectoAbiertoId(null)}
        onGuardarIdea={(idea, esCreate) => void guardarIdea(idea, esCreate)}
        onAlternarHecha={(idea) => void alternarHecha(idea)}
        onEliminarIdea={(id) => void eliminarIdea(id)}
      />
    );
  }

  const abrirNuevo = () => {
    setEditando(null);
    setMostrarFormulario(true);
  };

  const cerrarFormulario = () => {
    setMostrarFormulario(false);
    setEditando(null);
  };

  const onGuardar = (nombre: string) => {
    const ahora = new Date().toISOString();
    const proyecto: Proyecto = {
      id: editando?.id ?? nuevoUUID(),
      nombre,
      creado: editando?.creado ?? ahora,
      updatedAt: ahora,
      deletedAt: null,
    };
    void guardarProyecto(proyecto, editando === null);
    cerrarFormulario();
  };

  const confirmarEliminar = () => {
    if (proyectoAEliminar) void eliminarProyecto(proyectoAEliminar.id);
    setProyectoAEliminar(null);
  };

  const ideasDe = (proyectoId: string) => ideas.filter((i) => i.proyectoId === proyectoId);
  const totalPendientes = ideas.filter((i) => !i.hecha).length;
  const ideasAEliminar = proyectoAEliminar ? ideasDe(proyectoAEliminar.id).length : 0;

  return (
    <>
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <Lightbulb className="text-amber-500" size={32} />
              Ideas
            </h1>
            <p className="text-gray-600 mt-1">
              {proyectos.length} proyectos • {totalPendientes} ideas pendientes
            </p>
          </div>
          <button
            onClick={abrirNuevo}
            className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-md"
          >
            <Plus size={20} />
            Nuevo Proyecto
          </button>
        </div>
      </div>

      {mostrarFormulario && (
        <FormularioProyecto
          key={editando?.id ?? 'nuevo'}
          editando={editando}
          onGuardar={onGuardar}
          onCancelar={cerrarFormulario}
        />
      )}

      {proyectoAEliminar && (
        <DialogoConfirmacion
          icono={<Trash2 size={24} />}
          titulo="Borrar proyecto"
          mensaje={`¿Borrar "${proyectoAEliminar.nombre}"? Esta acción no se puede deshacer.`}
          detalle={
            ideasAEliminar > 0 ? (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3">
                <p className="text-sm text-yellow-800">
                  ⚠️ <strong>Advertencia:</strong> también se{' '}
                  {ideasAEliminar === 1 ? 'borrará 1 idea' : `borrarán ${ideasAEliminar} ideas`} con
                  sus anotaciones.
                </p>
              </div>
            ) : undefined
          }
          textoConfirmar="Sí, borrar"
          iconoBotonConfirmar={<Trash2 size={18} />}
          variante="peligro"
          onConfirmar={confirmarEliminar}
          onCancelar={() => setProyectoAEliminar(null)}
        />
      )}

      {proyectos.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg p-8 text-center text-gray-500 flex flex-col items-center gap-2">
          <FolderOpen size={32} className="text-gray-300" />
          Crea un proyecto para empezar a anotar ideas
        </div>
      ) : (
        <div className="space-y-4">
          {proyectos.map((proyecto) => {
            const delProyecto = ideasDe(proyecto.id);
            const hechas = delProyecto.filter((i) => i.hecha).length;
            return (
              <TarjetaProyecto
                key={proyecto.id}
                proyecto={proyecto}
                pendientes={delProyecto.length - hechas}
                hechas={hechas}
                onAbrir={(p) => setProyectoAbiertoId(p.id)}
                onEditar={(p) => {
                  setEditando(p);
                  setMostrarFormulario(true);
                }}
                onEliminar={(p) => setProyectoAEliminar(p)}
              />
            );
          })}
        </div>
      )}
    </>
  );
}
